/**
 * Raise Complaint: validate searched invoice against logged-in user's
 * assigned Customer Type (Domestic/Export) and Business Vertical(s).
 */
import {
  getLoggedInUserFromStorage,
  getUserAssignedMarketCodes,
  normalizeUserMarketCode,
} from "./userTerritoryScope";

export const INVOICE_CUSTOMER_TYPE_MISMATCH_MESSAGE =
  "The entered Invoice No./ODN No. does not belong to your assigned customer type (Domestic/Export). Please enter a valid Invoice No./ODN No.";

export const INVOICE_BUSINESS_VERTICAL_MISMATCH_MESSAGE =
  "You are not authorized to raise a complaint for this Business Vertical. Please search using an Invoice No./ODN No. belonging to your assigned Business Vertical.";

export const INITIATOR_GEO_MISMATCH_MESSAGE =
  "Your assigned geography does not match this invoice's location. You cannot raise a Service Request for this invoice. Please contact your administrator for assistance.";

const splitCsv = (v) =>
  typeof v === "string"
    ? v
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : Array.isArray(v)
      ? v.map((x) => String(x || "").trim()).filter(Boolean)
      : [];

const normalizeBvKey = (text) =>
  String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/** Map invoice MarketName/MarketCode to M001 (Domestic) or M002 (Export). */
export function normalizeInvoiceMarketCode(invoiceData) {
  if (!invoiceData) return null;
  const code =
    invoiceData.MarketCode ??
    invoiceData.marketCode ??
    invoiceData.MARKETCODE ??
    "";
  const name =
    invoiceData.MarketName ??
    invoiceData.marketName ??
    invoiceData.MARKETNAME ??
    "";
  return normalizeUserMarketCode(code, name);
}

export function getUserAssignedBusinessVerticalTokens(user) {
  if (!user) return { names: [], codes: [] };

  const names = [
    ...splitCsv(user.catgName ?? user.CatgName),
    ...splitCsv(user.bvName ?? user.BVName),
  ];
  const codes = [
    ...splitCsv(user.catgCode ?? user.CatgCode),
    ...splitCsv(user.bvCode ?? user.BVCode),
  ].filter((c) => c && c !== "0000");

  const uniqueNames = [...new Set(names.map((n) => n.trim()).filter(Boolean))];
  const uniqueCodes = [...new Set(codes.map((c) => c.trim()).filter(Boolean))];
  return { names: uniqueNames, codes: uniqueCodes };
}

/** Restrict BV dropdown options to categories assigned on the logged-in user. */
export function filterBusinessVerticalOptionsForUser(options, user) {
  const list = Array.isArray(options) ? options : [];
  const assigned = getUserAssignedBusinessVerticalTokens(user);
  const hasAssigned = assigned.names.length > 0 || assigned.codes.length > 0;
  if (!hasAssigned) return list;

  const codeSet = new Set(assigned.codes.map((c) => c.toUpperCase()));
  const nameKeys = new Set(assigned.names.map((n) => normalizeBvKey(n)));

  return list.filter((option) => {
    const id = String(option?.id ?? "").trim().toUpperCase();
    const text = normalizeBvKey(option?.text);
    return codeSet.has(id) || nameKeys.has(text) || nameKeys.has(normalizeBvKey(id));
  });
}

function getInvoiceProductsList(invoiceData) {
  if (!invoiceData) return [];
  let products = invoiceData.products ?? invoiceData.Products ?? [];
  if (typeof products === "string") {
    try {
      products = JSON.parse(products);
    } catch {
      return [];
    }
  }
  if (products && !Array.isArray(products)) {
    if (Array.isArray(products.products)) products = products.products;
    else if (Array.isArray(products.data)) products = products.data;
    else products = [];
  }
  return Array.isArray(products) ? products : [];
}

function getRowBusinessVerticalLabel(row) {
  return (
    row?.BusinessVertical ??
    row?.businessVertical ??
    row?.BVName ??
    row?.bvName ??
    ""
  )
    .toString()
    .trim();
}

/** Distinct non-empty Business Vertical labels from invoice line items. */
export function getInvoiceBusinessVerticals(invoiceData) {
  const found = [];
  const seen = new Set();
  for (const row of getInvoiceProductsList(invoiceData)) {
    const label = getRowBusinessVerticalLabel(row);
    if (!label) continue;
    const key = normalizeBvKey(label);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    found.push(label);
  }
  return found;
}

/** Distinct BV labels from an explicit product/line array (selected materials / draft lines). */
export function getBusinessVerticalsFromRows(rows) {
  const found = [];
  const seen = new Set();
  for (const row of Array.isArray(rows) ? rows : []) {
    const label = getRowBusinessVerticalLabel(row);
    if (!label) continue;
    const key = normalizeBvKey(label);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    found.push(label);
  }
  return found;
}

function isBusinessVerticalAuthorized(invoiceBv, assigned) {
  const invNorm = normalizeBvKey(invoiceBv);
  if (!invNorm) return true;

  if (assigned.codes.some((c) => normalizeBvKey(c) === invNorm)) return true;

  const assignedNorms = assigned.names.map(normalizeBvKey).filter(Boolean);
  if (assignedNorms.includes(invNorm)) return true;

  if (assigned.codes.map(normalizeBvKey).filter(Boolean).includes(invNorm)) {
    return true;
  }

  return false;
}

function validateBusinessVerticalList(invoiceBvs, loginUser) {
  const assignedBv = getUserAssignedBusinessVerticalTokens(loginUser);
  const hasAssignedBv = assignedBv.names.length > 0 || assignedBv.codes.length > 0;
  if (!hasAssignedBv) return null;
  if (!invoiceBvs.length) return null;

  const unauthorized = invoiceBvs.some(
    (bv) => !isBusinessVerticalAuthorized(bv, assignedBv)
  );
  if (unauthorized) {
    return INVOICE_BUSINESS_VERTICAL_MISMATCH_MESSAGE;
  }
  return null;
}

/**
 * @param {object} invoiceData
 * @param {object|null} user
 * @param {object[]|null} selectedRows When provided, BV is checked only on these lines (Save Draft / Next / Submit).
 * @returns {string|null} Error message when access should be denied; null when allowed.
 */
export function validateInvoiceAccessForUser(
  invoiceData,
  user = null,
  selectedRows = null
) {
  const loginUser = user || getLoggedInUserFromStorage();
  if (!invoiceData || !loginUser) return null;

  const assignedMarkets = getUserAssignedMarketCodes(loginUser);
  const invoiceMarket = normalizeInvoiceMarketCode(invoiceData);

  if (assignedMarkets.length > 0 && invoiceMarket) {
    if (!assignedMarkets.includes(invoiceMarket)) {
      return INVOICE_CUSTOMER_TYPE_MISMATCH_MESSAGE;
    }
  }

  const invoiceBvs =
    selectedRows != null
      ? getBusinessVerticalsFromRows(selectedRows)
      : getInvoiceBusinessVerticals(invoiceData);

  return validateBusinessVerticalList(invoiceBvs, loginUser);
}

/**
 * Validate only Business Vertical labels (e.g. checklist material lines / draft InvoiceDetails).
 */
export function validateBusinessVerticalsForUser(bvLabelsOrRows, user = null) {
  const loginUser = user || getLoggedInUserFromStorage();
  if (!loginUser) return null;

  const isPrimitiveList =
    Array.isArray(bvLabelsOrRows) &&
    bvLabelsOrRows.every(
      (x) => x == null || typeof x === "string" || typeof x === "number"
    );

  const labels = Array.isArray(bvLabelsOrRows)
    ? isPrimitiveList
      ? bvLabelsOrRows.map((x) => String(x || "").trim()).filter(Boolean)
      : getBusinessVerticalsFromRows(bvLabelsOrRows)
    : [];

  return validateBusinessVerticalList(labels, loginUser);
}
